import { Injectable, inject } from '@angular/core';

import {
    CashierNewOptions,
    CashierService as CoreCashierService,
    DslService,
    MenuAction,
    MenuActionOrigin,
    MenuActionsService,
    NavigationService,
    OnFeatureInit,
    PlatformProductMapConfig,
    ProductHomepagesConfig,
    ToastrQueueService,
    TrackingService,
    UserEvent,
    UserLoginEvent,
    UserService,
} from '@frontend/vanilla/core';
import { AccountMenuDataService } from '@frontend/vanilla/shared/account-menu';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { CashierConfig } from './cashier.client-config';
import { CashierService } from './cashier.service';
import { QuickDepositOverlayService } from './quick-deposit/quick-deposit-overlay.service';
import { QuickDepositAction, QuickDepositEvent } from './quick-deposit/quick-deposit.models';
import { QuickDepositService } from './quick-deposit/quick-deposit.service';

@Injectable()
export class CashierBootstrapService implements OnFeatureInit {
    private readonly accountMenuService = inject(AccountMenuDataService);
    private readonly cashierConfig = inject(CashierConfig);
    private readonly cashierService = inject(CashierService);
    private readonly coreCashierService = inject(CoreCashierService);
    private readonly dslService = inject(DslService);
    private readonly menuActionsService = inject(MenuActionsService);
    private readonly navigation = inject(NavigationService);
    private readonly platformProductMapConfig = inject(PlatformProductMapConfig);
    private readonly productHomepagesConfig = inject(ProductHomepagesConfig);
    private readonly quickDepositOverlayService = inject(QuickDepositOverlayService);
    private readonly quickDepositService = inject(QuickDepositService);
    private readonly toastrQueueService = inject(ToastrQueueService);
    private readonly trackingService = inject(TrackingService);
    private readonly user = inject(UserService);

    async onFeatureInit() {
        await Promise.all([firstValueFrom(this.cashierConfig.whenReady), firstValueFrom(this.platformProductMapConfig.whenReady)]);

        this.coreCashierService.set(this.cashierService);

        this.menuActionsService.register(MenuAction.GOTO_CASHIER, (origin: string) => {
            const options = this.getCashierOptions(origin);

            if (origin === MenuActionOrigin.Header) {
                // use deposit action here
                this.cashierService.goToCashierDeposit(options);
            } else {
                this.cashierService.goToCashier(options);
            }
        });
        this.menuActionsService.register(MenuAction.GOTO_DEPOSIT, async (origin: string) => {
            const hookDetected = await this.coreCashierService.runDepositHooks();
            if (hookDetected) return;

            const options = this.getCashierOptions(origin);
            options.skipQuickDeposit = !this.cashierConfig.quickDepositAllowedOrigins[origin.toLowerCase()];
            this.cashierService.handlePartialRegistration(origin, this.accountMenuService.routerModeReturnUrl, options);
        });
        this.menuActionsService.register(MenuAction.GOTO_WITHDRAWAL, (origin: string) => {
            const options = this.getCashierOptions(origin);
            this.cashierService.goToCashierWithdrawal(options);
        });
        this.menuActionsService.register(MenuAction.GOTO_MANAGE_MY_CARDS, (origin: string) => {
            const options = this.getCashierOptions(origin);
            this.cashierService.goToManageMyCards(options);
        });
        this.menuActionsService.register(MenuAction.GOTO_TRANSACTION_HISTORY, (origin: string) => {
            const options = this.getCashierOptions(origin);
            this.cashierService.goToTransactionHistory(options);
        });
        this.menuActionsService.register(MenuAction.GOTO_PAYMENT_PREFERENCES, (origin: string) => {
            const options = this.getCashierOptions(origin);
            this.cashierService.goToPaymentPreferences(options);
        });
        this.menuActionsService.register(MenuAction.DEPOSIT_PROMPT_ACTION, (origin: string) => {
            const options = this.getCashierOptions(origin);

            if (this.toastrQueueService.currentToast) {
                const contextToastContent = this.toastrQueueService.currentToast.content;
                this.trackingService.getContentItemTracking(contextToastContent.parameters, 'tracking.ActionEvent').subscribe((tracking) => {
                    if (tracking) {
                        options.customTracking = {
                            eventName: tracking.event,
                            data: tracking.data,
                        };
                    }
                });
            }

            this.cashierService.goToCashierDeposit(options);
        });
        this.menuActionsService.register(MenuAction.HEADER_BALANCE_ACTION, () => {
            if (!this.user.realPlayer) {
                this.cashierService.goToCashierDeposit({});
            } else if (!this.navigation.location.absUrl().includes('/mobileportal/balancebreakdown')) {
                //Todo: Avoid appending rurl multiple times to the url. Remove when fixed on Vanilla side
                this.navigation.goTo(this.productHomepagesConfig.portal + '/mobileportal/balancebreakdown', {
                    appendReferrer: this.navigation.location.absUrl(),
                });
            }
        });

        if (this.cashierConfig.isQuickDepositEnabledCondition) {
            this.dslService
                .evaluateExpression<boolean>(this.cashierConfig.isQuickDepositEnabledCondition)
                .pipe(take(1))
                .subscribe((isEnabled: boolean) => {
                    if (isEnabled) {
                        if (this.user.isAuthenticated) {
                            this.initQuickDeposit();
                        } else {
                            this.user.events
                                .pipe(filter((event: UserEvent) => event instanceof UserLoginEvent))
                                .subscribe(() => this.initQuickDeposit());
                        }
                    }
                });
        } else {
            if (this.cashierConfig.isQuickDepositEnabled) {
                if (this.user.isAuthenticated) {
                    this.initQuickDeposit();
                } else {
                    this.user.events.pipe(filter((event: UserEvent) => event instanceof UserLoginEvent)).subscribe(() => this.initQuickDeposit());
                }
            }
        }
    }

    private initQuickDeposit() {
        this.quickDepositService.events.subscribe((event: QuickDepositEvent) => {
            if (event.action === QuickDepositAction.Open) {
                this.quickDepositOverlayService.show(event.options);
            }
        });
    }

    private getCashierOptions(origin: string): CashierNewOptions {
        const trackerId = this.cashierConfig.trackerIds[origin.toLowerCase()] || '';
        const returnUrl = this.navigation.location.absUrl() || this.accountMenuService.routerModeReturnUrl;

        return {
            trackerId,
            returnUrl,
        };
    }
}
